import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChartLineUp, Plus, FolderOpen, CurrencyDollar, Target, FileText } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const PostGrantManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [postGrants, setPostGrants] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newGrant, setNewGrant] = useState({ application_id: "", awarded_amount: "" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pgRes, appsRes] = await Promise.all([
          axios.get(`${API}/post-grants`),
          axios.get(`${API}/applications`)
        ]);
        
        setPostGrants(pgRes.data.post_grants || []);
        // Filter to approved applications without existing post-grants
        const existingAppIds = new Set((pgRes.data.post_grants || []).map(pg => pg.application_id));
        const approvedApps = (appsRes.data.applications || []).filter(
          app => app.status === "approved" && !existingAppIds.has(app.application_id)
        );
        setApplications(approvedApps);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 403) {
          toast.error("Post-Grant Manager is a Premium feature");
          navigate("/pricing");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const createPostGrant = async () => {
    if (!newGrant.application_id || !newGrant.awarded_amount) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const res = await axios.post(`${API}/post-grants`, {
        application_id: newGrant.application_id,
        awarded_amount: parseFloat(newGrant.awarded_amount)
      });

      setPostGrants(prev => [res.data, ...prev]);
      setApplications(prev => prev.filter(a => a.application_id !== newGrant.application_id));
      setDialogOpen(false);
      setNewGrant({ application_id: "", awarded_amount: "" });
      toast.success("Post-grant project created");
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const calculateProgress = (pg) => {
    if (!pg.milestones || pg.milestones.length === 0) return 0;
    const completed = pg.milestones.filter(m => m.completed).length;
    return Math.round((completed / pg.milestones.length) * 100);
  };

  const calculateBudgetSpent = (pg) => {
    if (!pg.budget_items || pg.budget_items.length === 0) return 0;
    const totalSpent = pg.budget_items.reduce((sum, item) => sum + (item.spent || 0), 0);
    return totalSpent;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="post-grant-manager-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Post-Grant Manager</h1>
            <p className="text-zinc-400 mt-1">Track budgets, milestones, and reports for your awarded grants</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                disabled={applications.length === 0}
                data-testid="new-post-grant-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader>
                <DialogTitle>Create Post-Grant Project</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Select Approved Application</Label>
                  <Select value={newGrant.application_id} onValueChange={(v) => setNewGrant(prev => ({ ...prev, application_id: v }))}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-application">
                      <SelectValue placeholder="Choose application" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {applications.map((app) => (
                        <SelectItem key={app.application_id} value={app.application_id}>
                          {app.grant_title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Awarded Amount ($)</Label>
                  <Input 
                    type="number"
                    value={newGrant.awarded_amount}
                    onChange={(e) => setNewGrant(prev => ({ ...prev, awarded_amount: e.target.value }))}
                    placeholder="5000"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="input-awarded-amount"
                  />
                </div>
                <Button 
                  onClick={createPostGrant}
                  className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                  data-testid="create-post-grant-btn"
                >
                  Create Project
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {postGrants.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen weight="duotone" className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Post-Grant Projects</h3>
            <p className="text-zinc-500 mb-4">
              {applications.length > 0 
                ? "Create a project for your approved grant to start tracking"
                : "You'll be able to create projects once you have approved applications"}
            </p>
            {applications.length > 0 && (
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
              >
                Create Your First Project
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {postGrants.map((pg) => (
              <Card 
                key={pg.post_grant_id}
                className="bg-zinc-950 border-zinc-800 card-hover cursor-pointer"
                onClick={() => navigate(`/post-grants/${pg.post_grant_id}`)}
                data-testid={`post-grant-${pg.post_grant_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`status-${pg.status} px-2 py-0.5 rounded-sm text-xs font-mono uppercase`}>
                      {pg.status}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2">{pg.grant_title}</h3>
                  
                  {/* Awarded Amount */}
                  <div className="flex items-center gap-2 text-emerald-400 font-mono mb-4">
                    <CurrencyDollar className="w-4 h-4" />
                    <span>${pg.awarded_amount.toLocaleString()}</span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 text-center border-t border-zinc-800 pt-4">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Milestones</p>
                      <p className="text-lg font-mono text-white">{pg.milestones?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Spent</p>
                      <p className="text-lg font-mono text-white">${calculateBudgetSpent(pg).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Reports</p>
                      <p className="text-lg font-mono text-white">{pg.reports?.length || 0}</p>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                      <span>Progress</span>
                      <span>{calculateProgress(pg)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-bar-fill" 
                        style={{ width: `${calculateProgress(pg)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PostGrantManager;
